import React, { useEffect, useMemo, useState } from 'react';
import { Card, Empty, Input, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import BrandLogo from '../BrandLogo';
import { getPriceProducts, PriceProduct } from '../../api/priceList';

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

const formatPrice = (v: number | null | undefined): string => (v == null ? '—' : fmt.format(v));

const PriceListTab: React.FC = () => {
  const [products, setProducts] = useState<PriceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState<string | undefined>(undefined);
  const [group, setGroup] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getPriceProducts({
          brand: brand || undefined,
          search: search || undefined,
        });
        setProducts(data);
      } catch (e: any) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [brand, search]);

  const groups = useMemo(
    () => Array.from(new Set(products.map(p => p.group_name).filter(Boolean) as string[])),
    [products],
  );

  const filtered = useMemo(
    () => (group ? products.filter(p => p.group_name === group) : products),
    [products, group],
  );

  const columns: ColumnsType<PriceProduct> = [
    {
      title: 'Товар',
      key: 'name',
      render: (_, r) => (
        <Space size={6} wrap>
          <BrandLogo brand={r.brand} size="0.9em" />
          <span style={{ fontWeight: 500 }}>{r.model_name || r.price_name}</span>
          {r.catalog_id == null && <Tag color="orange">нет в каталоге</Tag>}
        </Space>
      ),
    },
    {
      title: 'Группа',
      dataIndex: 'group_name',
      key: 'group_name',
      width: 260,
      render: v => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Typography.Text>,
    },
    {
      title: 'Разлив, ₽/л',
      dataIndex: 'bulk_price_per_liter',
      key: 'bulk',
      width: 110,
      align: 'right',
      render: v => (v == null ? '—' : <b>{formatPrice(v)}</b>),
    },
    {
      title: 'Фасовки',
      key: 'sizes',
      render: (_, r) => (
        <Space size={4} wrap>
          {r.sizes.map(s => (
            <Tag
              key={s.id}
              color={s.price != null ? undefined : 'default'}
              style={s.price != null ? { marginInlineEnd: 0 } : { marginInlineEnd: 0, color: '#bbb', borderStyle: 'dashed' }}
            >
              {s.volume} {s.unit}{s.price != null ? ` — ${formatPrice(s.price)} ₽` : ''}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          placeholder="Бренд"
          style={{ minWidth: 140 }}
          value={brand}
          onChange={v => { setBrand(v); setGroup(undefined); }}
          options={['MOBIL', 'ZIC', 'SK', 'Oilway'].map(b => ({ value: b, label: b }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="Ценовая группа"
          style={{ minWidth: 360 }}
          value={group}
          onChange={setGroup}
          options={groups.map(g => ({ value: g, label: g }))}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Поиск по названию"
          style={{ width: 280 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Typography.Text type="secondary">Позиций: {filtered.length}</Typography.Text>
      </Space>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <Empty description="Прайс пуст. Запустите scripts/import_price_list.py на сервере." />
        </Card>
      ) : (
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          size="small"
          bordered
          pagination={false}
          scroll={{ x: 1000, y: 'calc(100vh - 320px)' }}
          sticky
        />
      )}
    </div>
  );
};

export default PriceListTab;
